/**
 * useCurrentUser — resolves { name, email, role } from the active auth context.
 *
 * In dev-auth mode (devAuthEnabled === true):
 *   - name/email are synthetic placeholders.
 *   - role is derived from getDevRole():
 *     'reviewer' → 'reviewer', 'admin' → 'admin', 'external' → null.
 *
 * In production (devAuthEnabled === false):
 *   - Reads useMsal().accounts[0] and its idTokenClaims.
 *   - Returns null when no account is active (not signed in).
 *   - Role derivation mirrors RequireRole.tsx: checks `roles` array claim first
 *     ('Reviewer' → 'reviewer', 'Admin' → 'admin', neither → null).
 *
 * The result is null when no identity is available (signed out / no account).
 */

import { useMsal } from '@azure/msal-react';
import {
  devAuthEnabled,
  devRoleOverrideEnabled,
  getDevRole,
  getDevRoleOverride,
} from '@/config/devAuth';

export interface CurrentUser {
  name: string;
  email: string;
  role: 'reviewer' | 'admin' | null;
}

const REVIEWER_ROLE = 'Reviewer';
const ADMIN_ROLE = 'Admin';

function deriveRoleFromClaims(claims: Record<string, unknown> | undefined): 'reviewer' | 'admin' | null {
  if (!claims) return null;

  // 1. App-role claim (explicit Entra app-role assignment — preferred).
  const roles = claims['roles'];
  if (Array.isArray(roles)) {
    if (roles.includes(ADMIN_ROLE)) return 'admin';
    if (roles.includes(REVIEWER_ROLE)) return 'reviewer';
  }

  // 2. Groups claim (Azure AD security group membership — group object IDs).
  //    Mirrors the backend isAdmin/isReviewer logic and RequireRole.tsx.
  const adminGroupId = import.meta.env.VITE_AZURE_ADMIN_GROUP_ID as string | undefined;
  const reviewerGroupId = import.meta.env.VITE_AZURE_REVIEWER_GROUP_ID as string | undefined;
  const groups = claims['groups'];
  if (Array.isArray(groups)) {
    if (adminGroupId && groups.includes(adminGroupId)) return 'admin';
    if (reviewerGroupId && groups.includes(reviewerGroupId)) return 'reviewer';
  }

  return null;
}

export function useCurrentUser(): CurrentUser | null {
  const { accounts } = useMsal();

  if (devAuthEnabled) {
    // Full MSAL bypass: use a synthetic identity.
    const devRole = getDevRole();
    const role: 'reviewer' | 'admin' | null =
      devRole === 'reviewer' ? 'reviewer' : devRole === 'admin' ? 'admin' : null;

    return {
      name: devRole === 'external' ? 'Dev User' : devRole === 'reviewer' ? 'Dev Reviewer' : 'Dev Admin',
      email: `dev-${devRole}@uhealth.utah.edu`,
      role,
    };
  }

  // Real MSAL account — read it first so both paths below can use it.
  const account = accounts[0];
  if (!account) return null;

  if (devRoleOverrideEnabled) {
    // UI-only override: use real name/email from MSAL but substitute the role.
    const override = getDevRoleOverride();
    if (override !== null) {
      const role: 'reviewer' | 'admin' | null =
        override === 'reviewer' ? 'reviewer' : override === 'admin' ? 'admin' : null;
      return {
        name: (account.name as string | undefined) ?? account.localAccountId ?? 'User',
        email: (account.username as string | undefined) ?? '',
        role,
      };
    }
  }

  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const role = deriveRoleFromClaims(claims);

  // name: MSAL AccountInfo.name (display name), fallback to localAccountId.
  const name =
    (account.name as string | undefined) ??
    (account.localAccountId as string | undefined) ??
    'User';

  // email: MSAL AccountInfo.username is the UPN (typically the email).
  const email = (account.username as string | undefined) ?? '';

  return { name, email, role };
}

export default useCurrentUser;
